import React from 'react';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { TaxRates, TaxRateSet, TaxRegime } from '@/api/models';
import {
    useCurrentTaxSettingsQuery,
    useRecordTaxChangeMutation,
    useTaxSettingsHistoryQuery,
} from '@/api/queries';
import { unwrapStatement } from '@/pages/accounting/statements/statementMeta';
import { getAuthMethod, getUser } from '@/utils/authSession';
import {
    canWriteSettings,
    deriveSettingsAccess,
    type SettingsAccess,
} from './settingsAccess';
import {
    applyRegimeDefaults,
    defaultEffectiveFrom,
    draftToRates,
    earliestEffectiveFrom,
    EMPTY_DRAFT,
    isDirty,
    isEffectiveFromValid,
    isRecoverableInPlace,
    needsNewClientId,
    newTaxRateSetId,
    NOTE_MAX_LENGTH,
    rateFieldError,
    RATE_FIELDS,
    saveErrorKey,
    todayIso,
    toRateDraft,
    type RateDraft,
} from './settingsMeta';

/**
 * What the page is actually showing.
 *
 * `onboarding` and `scheduled` both arise from the *same* 404 on `/current`,
 * and telling them apart is the whole reason the history is loaded alongside
 * it. One is a new tenant who needs an accounting period; the other is a tenant
 * whose rates are all future-dated. Collapsing them into one error page would
 * send the first to support for something they can self-serve.
 */
export type SettingsStatus =
    | 'loading'
    /** A set is in force today; the normal case. */
    | 'ready'
    /** Sets exist but every one is future-dated — scheduled, not yet in force. */
    | 'scheduled'
    /** Nothing recorded at all: no accounting period, so the seeder sat out. */
    | 'onboarding';

export type UseSettingsPageResult = {
    access: SettingsAccess;
    canWrite: boolean;
    status: SettingsStatus;
    isLoading: boolean;
    error: ErrorResponse | null;
    onRetry: () => void;

    /** The set in force today, if any. */
    currentSet: TaxRateSet | null;
    /** The latest set by effective date, **including future-dated ones**. */
    latestSet: TaxRateSet | null;

    regime: TaxRegime;
    draft: RateDraft;
    onRegimeChange: (regime: TaxRegime) => void;
    onRateChange: (key: keyof TaxRates, value: number | null) => void;

    effectiveFrom: string;
    /** Earliest legal effective date, or null when anything goes. */
    earliestEffectiveFrom: string | null;
    /** True when today is no longer a legal choice — worth explaining. */
    isTodayUnavailable: boolean;
    onEffectiveFromChange: (value: string) => void;

    note: string;
    onNoteChange: (value: string) => void;
    noteMaxLength: number;

    /** i18n key per rate, or null when the field is fine. */
    errors: Partial<Record<keyof TaxRates, string | null>>;
    isValid: boolean;
    isDirty: boolean;
    isSaving: boolean;

    /** i18n key for the save failure, or null. */
    saveErrorKey: string | null;
    /** A stale base set — offer to reload and re-apply without losing input. */
    canReloadAndReapply: boolean;
    onReloadAndReapply: () => Promise<void>;

    /** Set after a successful save, cleared on the next edit. */
    isSaved: boolean;
    onSave: () => Promise<void>;
    onReset: () => void;
};

/**
 * Drives the tax settings screen.
 *
 * The one decision worth reading carefully is **what `basedOnSetId` is**. It is
 * the id of the set in force *today* — `currentSet` — and not the latest set by
 * effective date. The server compares it against today's in-force set precisely
 * so that scheduling a future-dated change does not invalidate everyone's view
 * of today's rates; sending the latest instead would make every save after the
 * first future-dated change fail with `STALE_BASE_SET` forever.
 *
 * The *date* floor is the opposite: it comes from `latestSet`, because a new
 * set must be strictly after every existing one to keep the timeline monotonic.
 * Two different questions, two different sets.
 */
export const useSettingsPage = (): UseSettingsPageResult => {
    const access = React.useMemo(
        () => deriveSettingsAccess(getUser(), getAuthMethod()),
        [],
    );
    const canWrite = canWriteSettings(access);
    // A kiosk session 403s on every one of these endpoints, so the page must
    // not fetch at all — it explains the requirement instead.
    const isEnabled = access.kind === 'full' || access.kind === 'readOnly';

    const currentQuery = useCurrentTaxSettingsQuery();
    const historyQuery = useTaxSettingsHistoryQuery();
    const mutation = useRecordTaxChangeMutation();

    const current = unwrapStatement(currentQuery.data, currentQuery.error);
    const history = unwrapStatement(historyQuery.data, historyQuery.error);

    const currentSet = current.data ?? null;
    const sets = React.useMemo(() => history.data?.sets ?? [], [history.data]);
    const latestSet = sets[0] ?? null;

    // A 404 from /current is an expected outcome, not a page error — it is the
    // onboarding and scheduled cases. Every other failure is a real error.
    const currentIsAbsent = current.error?.status === 404;
    const error = React.useMemo(() => {
        if (!isEnabled) {
            return null;
        }
        if (current.error && !currentIsAbsent) {
            return current.error;
        }
        return history.error ?? null;
    }, [isEnabled, current.error, currentIsAbsent, history.error]);

    const isLoading =
        isEnabled && (currentQuery.isLoading || historyQuery.isLoading);

    const status: SettingsStatus = React.useMemo(() => {
        if (isLoading) {
            return 'loading';
        }
        if (currentSet) {
            return 'ready';
        }
        if (currentIsAbsent) {
            return sets.length > 0 ? 'scheduled' : 'onboarding';
        }
        return 'loading';
    }, [isLoading, currentSet, currentIsAbsent, sets.length]);

    /**
     * The set the form is seeded from. Today's set when there is one; the
     * scheduled set otherwise, so the form opens on the rates that are about to
     * apply rather than on blanks.
     */
    const baseSet = currentSet ?? latestSet;

    const [regime, setRegime] = React.useState<TaxRegime | null>(null);
    const [draft, setDraft] = React.useState<RateDraft | null>(null);
    const [effectiveFrom, setEffectiveFrom] = React.useState<string>('');
    const [note, setNote] = React.useState('');
    const [saveError, setSaveError] = React.useState<string | undefined>(
        undefined,
    );
    const [isSaved, setIsSaved] = React.useState(false);
    // One id per pending edit, so a retry is idempotent rather than a duplicate.
    const clientIdRef = React.useRef<string>(newTaxRateSetId());

    // Adopt the loaded settings once. Keyed on the set object so a refetch that
    // returns the same values does not stamp over an in-progress edit — only a
    // genuinely new payload re-seeds the form.
    const adoptedRef = React.useRef<TaxRateSet | null | undefined>(undefined);
    React.useEffect(() => {
        if (isLoading || adoptedRef.current === baseSet) {
            return;
        }
        adoptedRef.current = baseSet;
        setRegime(baseSet?.regime ?? 'STANDARD');
        setDraft(baseSet ? toRateDraft(baseSet.rates) : { ...EMPTY_DRAFT });
        setEffectiveFrom(defaultEffectiveFrom(latestSet));
        setNote('');
    }, [isLoading, baseSet, latestSet]);

    const clearSaveState = React.useCallback(() => {
        setSaveError(undefined);
        setIsSaved(false);
    }, []);

    const onRegimeChange = React.useCallback(
        (next: TaxRegime) => {
            clearSaveState();
            setRegime(next);
            // Picking a regime fills its two distribution rates and leaves the
            // statutory ones exactly as they were — see `applyRegimeDefaults`.
            // Both remain editable afterwards.
            setDraft((currentDraft) =>
                currentDraft
                    ? applyRegimeDefaults(next, currentDraft)
                    : currentDraft,
            );
        },
        [clearSaveState],
    );

    const onRateChange = React.useCallback(
        (key: keyof TaxRates, value: number | null) => {
            clearSaveState();
            setDraft((currentDraft) =>
                currentDraft ? { ...currentDraft, [key]: value } : currentDraft,
            );
        },
        [clearSaveState],
    );

    const onEffectiveFromChange = React.useCallback(
        (value: string) => {
            clearSaveState();
            setEffectiveFrom(value);
        },
        [clearSaveState],
    );

    const onNoteChange = React.useCallback(
        (value: string) => {
            clearSaveState();
            setNote(value.slice(0, NOTE_MAX_LENGTH));
        },
        [clearSaveState],
    );

    const errors = React.useMemo<
        Partial<Record<keyof TaxRates, string | null>>
    >(() => {
        if (!draft) {
            return {};
        }
        const next: Partial<Record<keyof TaxRates, string | null>> = {};
        for (const field of RATE_FIELDS) {
            next[field.key] = rateFieldError(draft[field.key]);
        }
        return next;
    }, [draft]);

    const earliest = earliestEffectiveFrom(latestSet);
    const isDateValid = isEffectiveFromValid(effectiveFrom, latestSet);
    const isValid =
        draft !== null &&
        draftToRates(draft) !== null &&
        isDateValid &&
        note.length <= NOTE_MAX_LENGTH;

    const dirty = React.useMemo(
        () =>
            draft !== null &&
            regime !== null &&
            isDirty(currentSet ?? latestSet, regime, draft),
        [draft, regime, currentSet, latestSet],
    );

    const onSave = React.useCallback(async () => {
        if (!draft || !regime || !isValid || !canWrite) {
            return;
        }
        const rates = draftToRates(draft);
        if (!rates) {
            return;
        }
        setSaveError(undefined);

        const result = await mutation.mutateAsync({
            id: clientIdRef.current,
            effectiveFrom,
            regime,
            rates,
            note: note.trim() ? note.trim() : null,
            // Today's in-force set — NOT the latest by effective date. See the
            // hook docblock; getting this wrong deadlocks the save button.
            basedOnSetId: currentSet?.id ?? null,
        });

        if (!result.success) {
            const code = result.error.code;
            if (needsNewClientId(code)) {
                // The id collided with different content. A fresh id fixes it
                // and the user keeps everything they typed.
                clientIdRef.current = newTaxRateSetId();
            }
            setSaveError(code ?? 'UNKNOWN');
            return;
        }

        // Recorded (or replayed). Start a fresh id for the next edit, and let
        // the invalidated queries re-seed the form from what the server stored.
        clientIdRef.current = newTaxRateSetId();
        adoptedRef.current = undefined;
        setIsSaved(true);
    }, [
        draft,
        regime,
        isValid,
        canWrite,
        mutation,
        effectiveFrom,
        note,
        currentSet,
    ]);

    /**
     * Recover from a stale base set **without losing the edit**.
     *
     * Refetch, re-point the form at the new base set, and keep every rate, the
     * date and the note exactly as typed. The user then presses Save again.
     * Discarding their work because another writer got there first would be
     * punishing them for someone else's timing.
     */
    const onReloadAndReapply = React.useCallback(async () => {
        setSaveError(undefined);
        await Promise.all([currentQuery.refetch(), historyQuery.refetch()]);
        // Block the adopt-effect from re-seeding: the draft is the user's, and
        // only the base set it is measured against has moved.
        adoptedRef.current = null;
        clientIdRef.current = newTaxRateSetId();
    }, [currentQuery, historyQuery]);

    const onReset = React.useCallback(() => {
        clearSaveState();
        setRegime(baseSet?.regime ?? 'STANDARD');
        setDraft(baseSet ? toRateDraft(baseSet.rates) : { ...EMPTY_DRAFT });
        setEffectiveFrom(defaultEffectiveFrom(latestSet));
        setNote('');
        clientIdRef.current = newTaxRateSetId();
    }, [clearSaveState, baseSet, latestSet]);

    const onRetry = React.useCallback(() => {
        currentQuery.refetch();
        historyQuery.refetch();
    }, [currentQuery, historyQuery]);

    return {
        access,
        canWrite,
        status,
        isLoading,
        error,
        onRetry,
        currentSet,
        latestSet,
        regime: regime ?? baseSet?.regime ?? 'STANDARD',
        draft: draft ?? { ...EMPTY_DRAFT },
        onRegimeChange,
        onRateChange,
        effectiveFrom,
        earliestEffectiveFrom: earliest,
        // Worth saying out loud: once a future-dated change is scheduled, today
        // stops being a legal effective date. The field silently opens on a
        // later day, and an unexplained jump reads as a bug.
        isTodayUnavailable: earliest !== null && earliest > todayIso(),
        onEffectiveFromChange,
        note,
        onNoteChange,
        noteMaxLength: NOTE_MAX_LENGTH,
        errors,
        isValid,
        isDirty: dirty,
        isSaving: mutation.isPending,
        saveErrorKey: saveError === undefined ? null : saveErrorKey(saveError),
        canReloadAndReapply: isRecoverableInPlace(saveError),
        onReloadAndReapply,
        isSaved,
        onSave,
        onReset,
    };
};
